import type { ApiClient } from 'clients/yogi-api'
const PROJECT_DETAILS_URL = '/project'
const PROJECT_SOURCE_GROUP_URL = '/project/sources/group'
const PROJECT_SOURCE_GROUP_INFO_URL = '/project/sources/group/info'
const SOURCE_UPDATE_URL = '/source/update'
const SOURCE_CUSTOMFIELD_DELETE_URL = '/source/field'
const POST_ADD_CUSTOM_FIELD = '/post/field/value'
import type {
  SourceCustomFieldAdd,
  SourceCustomFieldDelete,
  SourceDetailsVariables,
  SourceGroup,
  SourcesInfo,
} from 'features/sourcepage/types'
import type { ProjectDetails } from 'types/project'

export class SourcePageService {
  constructor(private client: ApiClient) {}

  /**
   * Gets the source group info within the name (Ex. Amazon)
   * @param key
   * @param sourcename name of the source
   * @param syndicated_source whether or not syndicated sources
   * @param proj_uuid uuid of project
   * @param country optional country to scope the source list (e.g. "Australia")
   */
  async getSourceGroupInfo(
    _key: string,
    sourcename: string,
    syndicated_source: boolean,
    proj_uuid: string,
    country?: string,
  ): Promise<SourcesInfo> {
    const { data } = await this.client.get<SourcesInfo>(
      PROJECT_SOURCE_GROUP_INFO_URL,
      {
        params: {
          proj_uuid: proj_uuid,
          source_name: sourcename,
          syndicated_sources: syndicated_source,
          country: country,
        },
      },
    )
    return data
  }

  /**
   * Gets the Source Groups
   * @param key
   * @param syndicated_sources gets the syndicated sources
   * @param proj_uuid gets the proj_uuid
   */
  async getSourceGroups(
    _key: string,
    syndicated_sources: boolean,
    proj_uuid: string,
  ): Promise<SourceGroup[]> {
    const { data } = await this.client.get<SourceGroup[]>(
      PROJECT_SOURCE_GROUP_URL,
      {
        params: {
          proj_uuid: proj_uuid,
          syndicated_sources: syndicated_sources,
        },
      },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<ProjectDetails>} List of projects
   */
  async getProjectDetails(
    _key: string,
    proj_uuid: string,
  ): Promise<ProjectDetails> {
    const { data } = await this.client.get<ProjectDetails>(
      PROJECT_DETAILS_URL,
      {
        params: { proj_uuid },
      },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<SourceDetails>} List of projects
   */
  async updateSource({
    uuid,
    title,
    description,
  }: SourceDetailsVariables): Promise<unknown> {
    const { data } = await this.client.put(
      SOURCE_UPDATE_URL,
      { title, description },
      { params: { uuid } },
    )
    return data
  }

  /**
   * Fetches project details
   * @param {string} proj_uuid - The project UUID
   * @returns {Promise<SourceDetails>} List of projects
   */
  async deleteSource({
    uuids,
    fields,
  }: SourceCustomFieldDelete): Promise<unknown> {
    const { data } = await this.client.delete(SOURCE_CUSTOMFIELD_DELETE_URL, {
      data: { uuids, fields },
    })
    return data
  }

  /**
   * Add Custom Field
   *
   */
  async addCustomField({
    resource_uuid,
    field,
    resource,
    field_value,
    field_type,
    lookup_field,
    lookup_type,
    add_field,
  }: SourceCustomFieldAdd): Promise<unknown> {
    const { data } = await this.client.post(
      POST_ADD_CUSTOM_FIELD,
      {
        resource_uuid,
        field,
        resource,
        field_value,
        field_type,
        lookup_field,
        lookup_type,
        add_field,
      },
      { params: { resource_uuid } },
    )
    return data
  }
}
